import { router } from 'expo-router';
import { NativeScrollEvent, NativeSyntheticEvent, StyleSheet, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { Divider } from 'react-native-paper';
import { useSharedValue } from 'react-native-reanimated';

import { useGetUserImpactScrolling } from '@/api/hooks/donationHooks';
import { i18n } from '@/utils/i18n';

import DotsLoader from '../DotsLoader';
import ListEmptyComponent from '../ListEmptyComponent';
import NextPageFetchingIndicator from '../NextPageFetchingIndicator';
import ImpactItemList from './ImpactItemList';

interface ImpactListProps {
  npoType: 'charity' | 'church';
  periodTime: 'month' | 'year' | 'all';
}

const ImpactList = ({ npoType, periodTime }: ImpactListProps) => {
  const { data, isPending, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage, refetch, isRefetching } =
    useGetUserImpactScrolling({
      npoType: npoType,
      periodTime: periodTime,
      n: '0',
      limit: 5
    });

  const impactData = data?.pages.flatMap((page) => page.results);

  const scrollOffset = useSharedValue(0);
  const isScrolling = useSharedValue(false);

  //Locales
  const empty_list = i18n.t('profile.impact.empty_donations');

  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const y = e.nativeEvent.contentOffset.y;

    if (scrollOffset.value > y) {
      if (isScrolling.value) {
        router.setParams({ showMapPill: 'true' });
      }
    } else if (scrollOffset.value < y) {
      if (isScrolling.value) {
        router.setParams({ showMapPill: 'false' });
      }
    }

    scrollOffset.value = y;
  };

  const onScrollBeginDrag = () => {
    isScrolling.value = true;
  };

  const onScrollEndDrag = () => {
    isScrolling.value = false;
  };

  return (
    <View style={styles.container}>
      {isPending ? (
        <DotsLoader />
      ) : (
        <FlatList
          style={styles.trendingList}
          showsVerticalScrollIndicator={false}
          bounces={false}
          data={impactData}
          scrollEnabled={true}
          onScrollBeginDrag={onScrollBeginDrag}
          onScrollEndDrag={onScrollEndDrag}
          onScroll={onScroll}
          keyExtractor={(item) => item.transactionId}
          contentContainerStyle={styles.listContentContainer}
          onEndReachedThreshold={0.6}
          onEndReached={(_) => {
            hasNextPage && !isFetching && fetchNextPage();
          }}
          onRefresh={refetch}
          refreshing={isRefetching}
          renderItem={({ item }) => (
            <ImpactItemList
              npoId={item.nonProfitOrgId}
              impactTitle={item.nonProfitOrgs.name}
              impactImage={item.nonProfitOrgs.thumbnail}
              impactAmount={item.amount}
              impactDate={item.createdAt}
              onCardPress={() => {
                router.navigate({
                  pathname: '/donation',
                  params: {
                    npoId: item.nonProfitOrgId,
                    npoName: item.nonProfitOrgs.name,
                    amount: 0
                  }
                });
              }}
            />
          )}
          ItemSeparatorComponent={() => <Divider />}
          ListEmptyComponent={<ListEmptyComponent label={empty_list} />}
          ListFooterComponent={isFetchingNextPage ? <NextPageFetchingIndicator /> : null}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  trendingList: {
    width: '100%'
  },
  listContentContainer: {
    paddingHorizontal: 18
  }
});

export default ImpactList;
